import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedLine } from "./MaskedReveal";
import { SHOWREEL } from "../data/siteData";

export const Hero = ({ onRequest }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["-4%", "8%"]);

  return (
    <section ref={ref} id="nachalo" className="relative overflow-hidden pt-16" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }} aria-hidden="true">
        <video
          src={SHOWREEL}
          autoPlay
          muted
          loop
          playsInline
          className="h-full w-full scale-105 object-cover"
          style={{ filter: "grayscale(1) sepia(0.28) contrast(0.9) brightness(0.5)" }}
        />
        <span className="absolute inset-0 bg-[#14110D]/55" />
        <span
          className="absolute inset-0"
          style={{ background: "linear-gradient(to bottom, rgba(20,17,13,0.35), rgba(20,17,13,0.62) 65%, #F4F1EA 98%)" }}
        />
      </motion.div>

      <div className="relative mx-auto flex min-h-[calc(100vh-4rem)] max-w-[1440px] flex-col justify-center px-5 pb-16 pt-14 sm:px-8 lg:pt-20">
        <motion.div style={{ y: textY }} className="w-full max-w-5xl">
          <MaskedLine delay={0.1}>
            <span className="mb-6 inline-flex w-fit items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-[#F4F1EA]/70">
              <span className="h-px w-10 bg-[#F4F1EA]/50" />
              Том I — Кинематография и филм
            </span>
          </MaskedLine>

          <h1 data-testid="hero-title" className="font-serif leading-[0.92] tracking-tight">
            <MaskedLine delay={0.22} className="text-[clamp(3rem,10vw,7.5rem)]">
              <span className="block font-semibold uppercase text-[#F4F1EA]">Визия</span>
            </MaskedLine>
            <MaskedLine delay={0.34} className="text-[clamp(2.2rem,8.4vw,6.6rem)]">
              <span className="block whitespace-nowrap pl-[0.08em] -mt-[0.06em] font-semibold uppercase italic leading-[1.05] text-[#C8402A]">
                и резултати
              </span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subline"
            className="mt-8 max-w-xl text-base leading-relaxed text-[#F4F1EA]/85 sm:text-lg"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            Рекламни филми и видео за брандове, които не просто изглеждат добре, а водят до запитвания и поръчки. Заснемаме из България и цяла Европа.
          </motion.p>

          <motion.div
            className="mt-9 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.68, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <button
              data-testid="hero-cta-explore"
              onClick={onRequest}
              className="group flex items-center gap-2 rounded-full bg-[#F4F1EA] px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#14110D] transition-transform duration-300 hover:-translate-y-0.5"
            >
              Започни проект
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <a
              data-testid="hero-cta-script"
              href="#raboti"
              className="rounded-full border border-[#F4F1EA]/40 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#F4F1EA] transition-colors duration-300 hover:bg-[#F4F1EA] hover:text-[#14110D]"
            >
              Гледай шоурийл
            </a>
          </motion.div>

          <motion.button
            data-testid="hero-dates-card"
            onClick={onRequest}
            className="group mt-10 flex w-fit items-center gap-5 rounded-2xl border border-[#F4F1EA]/20 bg-[#F4F1EA]/90 px-6 py-4 text-left backdrop-blur-sm transition-colors duration-300 hover:border-[#C8402A]"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#2F7D4F] opacity-60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#2F7D4F]" />
            </span>
            <span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C867E]">Резервирай</span>
              <span className="block font-serif text-xl text-[#14110D]">Свободни дати</span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C867E]">2026 · Снимачен ден</span>
            </span>
            <ArrowUpRight className="h-5 w-5 text-[#14110D]/40 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-[#C8402A]" />
          </motion.button>
        </motion.div>
      </div>

      <div className="relative mx-auto flex max-w-[1440px] items-center justify-between gap-4 px-5 pb-8 sm:px-8">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F4F1EA]/70">Свободен за 2026</span>
        <motion.a
          data-testid="hero-scroll-cue"
          href="#raboti"
          className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#F4F1EA]"
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        >
          Превърти <ArrowDown className="h-3.5 w-3.5" />
        </motion.a>
      </div>

      <motion.p
        data-testid="hero-byline"
        className="pointer-events-none absolute bottom-6 right-6 font-serif text-sm italic text-[#F4F1EA]/75 sm:bottom-8 sm:right-8 sm:text-base"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 1 }}
      >
        by Alex Cholakov
      </motion.p>
    </section>
  );
};
